import type { PickupLocation } from "../transporter/transporter";

/**
 * Stage a {@link Contact} is currently in within a CRM pipeline (sales or inventory).
 *  - `prospect`    — captured before any lead has been created for them (or only `lost`).
 *  - `opportunity` — has **at least one active** lead (not terminal); beats historical wins.
 *  - `customer`    — has closed/won before, with **no** active pipeline leads now.
 *  - `unqualified` — manually marked as not a fit; never set automatically by lead sync.
 *
 * Sales terminal statuses: `closed` / `lost`. Inventory: `won` / `lost`.
 * Sales and inventory use the same stage set and rules for now.
 */
export type ContactStage = "prospect" | "opportunity" | "customer" | "unqualified";

/**
 * Opt-in status for marketing email. A global unsubscribe always wins over
 * campaign audience filters. Missing on legacy contacts → subscribed.
 */
export type MarketingEmailStatus = "subscribed" | "unsubscribed";

export const MARKETING_EMAIL_STATUSES: MarketingEmailStatus[] = [
  "subscribed",
  "unsubscribed",
];

export const MARKETING_EMAIL_STATUS_LABEL: Record<MarketingEmailStatus, string> = {
  subscribed: "Subscribed",
  unsubscribed: "Unsubscribed",
};

/** Resolve marketing status; legacy docs without the field are subscribed. */
export function resolveContactMarketingEmailStatus(
  contact:
    | Pick<Contact, "marketingEmailStatus">
    | { marketingEmailStatus?: MarketingEmailStatus },
): MarketingEmailStatus {
  return contact.marketingEmailStatus === "unsubscribed"
    ? "unsubscribed"
    : "subscribed";
}

/** Suppress flag; missing on legacy docs → not suppressed. */
export function resolveContactMarketingEmailSuppressed(
  contact:
    | Pick<Contact, "marketingEmailSuppressed">
    | { marketingEmailSuppressed?: boolean },
): boolean {
  return contact.marketingEmailSuppressed === true;
}

/** One point-in-time stage assignment for a contact pipeline history. */
export interface ContactStageEntry {
  stage: ContactStage;
  changedAt: Date;
  /** User who caused the change, when known (manual edit). Omitted for automated lead sync. */
  changedByUserId?: string;
}

/**
 * CRM side an activity belongs to. Contacts/companies are shared across selling
 * and buying; activities are partitioned so timelines can filter Sales vs Inventory.
 * `"marketing"` is campaign mail (and later open/click/unsub) and must not bump
 * {@link Contact.lastActivityAt}. Missing {@link ContactActivity.domain} on
 * legacy docs is treated as `"sales"`.
 */
export type CrmActivityDomain = "sales" | "inventory" | "marketing";

/**
 * Activity kinds logged against a {@link Contact}.
 * For `task`/`lead`, {@link ContactActivity.note} holds a short label and the doc id lives on
 * {@link ContactActivity.taskId} / {@link ContactActivity.leadId}.
 */
export type ContactActivityType =
  | "call"
  | "sms"
  | "email"
  | "note"
  | "task"
  | "lead"
  | "shipping_repairs";

/** Lightweight history entry stored under {@link Contact.activities}. */
export interface ContactActivity {
  type: ContactActivityType;
  /** Free-text body. For `task`, this is the task's title for quick display. */
  note: string;
  /** Linked task doc id when {@link type} is `task`. */
  taskId?: string;
  /** Linked lead doc id when {@link type} is `lead`. */
  leadId?: string;
  /** Linked marketing campaign when this row is campaign mail. */
  campaignId?: string;
  /**
   * Sales vs inventory vs marketing partition. Omit on legacy docs (treated as `"sales"`).
   */
  domain?: CrmActivityDomain;
  createdAt: Date;
  createdByUserId: string;
}

/** Resolve activity domain; legacy entries without a domain are sales. */
export function resolveCrmActivityDomain(
  domain: CrmActivityDomain | undefined | null,
): CrmActivityDomain {
  if (domain === "inventory") return "inventory";
  if (domain === "marketing") return "marketing";
  return "sales";
}

/** Marketing mail must not reset the sales follow-up clock. */
export function activityBumpsLastActivityAt(
  domain: CrmActivityDomain | undefined | null,
): boolean {
  return resolveCrmActivityDomain(domain) !== "marketing";
}

/**
 * Matches {@link salesReps} entry `id` values (number or string).
 * `null` means the contact has not yet been claimed by a rep.
 */
export type ContactOwnerId = string | number | null;

/**
 * Centralized CRM record for a single person (and the primary company they
 * represent). Replaces the previous split between `prospects` and the derived
 * customer view that aggregated leads/sold trucks.
 *
 * Stored in the top-level Firestore collection `contacts`.
 */
export interface Contact {
  id: string;
  /** Display name, e.g. "Jane Doe". */
  name: string;
  /** Primary email (optional). */
  email?: string;
  /**
   * Alternate email (optional). Used when a contact has a second address
   * (e.g. after merging duplicates). Counts toward duplicate-email checks.
   */
  secondaryEmail?: string;
  /** Primary phone (optional). */
  phone?: string;
  /** Birthday as an ISO date string (`YYYY-MM-DD`), when known. */
  birthday?: string;
  /**
   * Sales pipeline owner (legacy {@link ownerId}).
   * `null` = unclaimed on the sales side.
   */
  ownerId: ContactOwnerId;
  /**
   * Inventory (buying) pipeline owner.
   * `null` = unclaimed on the inventory side. Missing on legacy docs → `null`.
   */
  inventoryOwnerId: ContactOwnerId;
  /**
   * Sales pipeline stage (latest entry in {@link stageHistory}).
   * Legacy contacts only stored this field — it remains the sales stage.
   */
  stage: ContactStage;
  /**
   * Sales stage changes over time, oldest first. Used to show the contact's
   * sales stage on past activity entries. When missing on legacy docs, the
   * converter synthesizes a single entry from {@link stage} + {@link createdAt}.
   */
  stageHistory?: ContactStageEntry[];
  /**
   * Inventory (buying) pipeline stage (latest entry in {@link inventoryStageHistory}).
   * Same stage values and rules as sales for now. Defaults to `prospect` on legacy docs.
   */
  inventoryStage: ContactStage;
  /**
   * Inventory stage changes over time, oldest first. When missing on legacy
   * docs, the converter synthesizes a single entry from {@link inventoryStage}
   * (or `prospect`) + {@link createdAt}.
   */
  inventoryStageHistory?: ContactStageEntry[];
  /** Optional deliver-to / contact location, same shape as lead dropoff. */
  dropoffLocation?: PickupLocation;
  /**
   * Marketing email opt-in. Overrides any campaign audience filter when
   * `unsubscribed`. Missing on legacy docs → treated as `subscribed`.
   */
  marketingEmailStatus: MarketingEmailStatus;
  /**
   * Hard suppress for marketing send. Independent of
   * {@link marketingEmailStatus}. Missing on legacy docs → false.
   */
  marketingEmailSuppressed?: boolean;
  /**
   * Timestamp of the most recent activity (note added, lead update, stage
   * change, etc.) — used to sort the CRM list. `null` when no activity is
   * recorded yet.
   */
  lastActivityAt: Date | null;
  /** Lead document ids associated with this contact. */
  leadIds: string[];
  /**
   * Recent activity log for this contact (calls, SMS, emails, notes, tasks).
   * Newest entries are typically appended last but consumers should sort by
   * {@link ContactActivity.createdAt} when rendering.
   */
  activities: ContactActivity[];
  /** Firebase user id of the matched portal client, when known. */
  userId?: string;
  /** Firebase Storage path to front of driver's license, when uploaded for this contact (.webp or .pdf). */
  driver_license_front_storage_path?: string;
  createdAt: Date;
  updatedAt: Date;
  createdByUserId: string;
}

/** Sales stage for a contact (`stage` is the legacy / sales field). */
export function resolveContactSalesStage(
  contact: Pick<Contact, "stage">,
): ContactStage {
  return contact.stage;
}

/** Inventory stage for a contact; legacy docs without the field are `prospect`. */
export function resolveContactInventoryStage(
  contact: Pick<Contact, "inventoryStage"> | { inventoryStage?: ContactStage },
): ContactStage {
  return contact.inventoryStage ?? "prospect";
}

/** Sales owner (`ownerId` is the legacy field). */
export function resolveContactSalesOwnerId(
  contact: Pick<Contact, "ownerId">,
): ContactOwnerId {
  return contact.ownerId ?? null;
}

/** Inventory owner; legacy docs without the field are unassigned (`null`). */
export function resolveContactInventoryOwnerId(
  contact:
    | Pick<Contact, "inventoryOwnerId">
    | { inventoryOwnerId?: ContactOwnerId },
): ContactOwnerId {
  return contact.inventoryOwnerId ?? null;
}

/** Owner for the given CRM domain (sales uses legacy {@link Contact.ownerId}). */
export function resolveContactOwnerIdForDomain(
  contact: Pick<Contact, "ownerId" | "inventoryOwnerId"> | Contact,
  domain: CrmActivityDomain,
): ContactOwnerId {
  return domain === "inventory"
    ? resolveContactInventoryOwnerId(contact)
    : resolveContactSalesOwnerId(contact);
}

/** Stage for the given CRM domain (sales uses legacy {@link Contact.stage}). */
export function resolveContactStageForDomain(
  contact: Pick<Contact, "stage" | "inventoryStage"> | Contact,
  domain: CrmActivityDomain,
): ContactStage {
  return domain === "inventory"
    ? resolveContactInventoryStage(contact)
    : resolveContactSalesStage(contact);
}

/**
 * Coerce legacy Firestore `userId` values (e.g. numeric SQL-era ids) to a
 * trimmed string. Returns empty when missing or not a scalar.
 */
export function normalizeContactUserId(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value).trim();
  }
  return "";
}

/** Whether the contact is linked to a portal client account. */
export function hasContactPortalAccount(userId: unknown): boolean {
  return normalizeContactUserId(userId).length > 0;
}

const BIRTHDAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Normalize a stored birthday to `YYYY-MM-DD`, or `undefined` when invalid. */
export function normalizeContactBirthday(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!BIRTHDAY_PATTERN.test(trimmed)) return undefined;
  const [year, month, day] = trimmed.split("-").map((part) => Number(part));
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return undefined;
  }
  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return undefined;
  }
  return trimmed;
}

const BIRTHDAY_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/** Human-readable birthday label, e.g. `March 15, 1985`. */
export function formatContactBirthday(
  birthday: string | undefined,
  options?: { includeYear?: boolean },
): string {
  const normalized = normalizeContactBirthday(birthday);
  if (!normalized) return "";
  const [, month, day] = normalized.split("-");
  const monthIndex = Number(month) - 1;
  const dayNumber = Number(day);
  if (monthIndex < 0 || monthIndex > 11 || !Number.isInteger(dayNumber) || dayNumber < 1) {
    return "";
  }
  const monthLabel = BIRTHDAY_MONTHS[monthIndex];
  const includeYear = options?.includeYear ?? true;
  if (!includeYear) return `${monthLabel} ${dayNumber}`;
  const year = normalized.slice(0, 4);
  return `${monthLabel} ${dayNumber}, ${year}`;
}
